/**
 * `ProjectModelAliases.ts` — the library side of part name aliases: detecting
 * that this project's copy of a model has drifted from the library entry, and
 * pulling the library's aliases back down.
 *
 * (The alias EDIT path needs no partial of its own — an alias change is just
 * another `EntitySnapshotPatch` field, so it rides `updateEntitySnapshotAsync`
 * with every other snapshot edit. This file exists for the comparison, which
 * is genuinely new behaviour.)
 *
 * **Why the check is free.** A part row (`#asset:{assetId}:part:{partId}`) IS
 * the library entry — Save to Library bakes onto exactly these rows. Loading a
 * scene already fetches them to build the parts catalog, and `ProjectModel`
 * retains their alias lists in `m_partAliasesById`. So the comparison is
 * in-memory: library aliases (part row) vs this project's aliases (snapshot
 * row). No extra Dynamo reads, no version bookkeeping, no manifest.
 *
 * **What counts as out of sync, and what deliberately does not.**
 *
 *  - A part is compared ONLY when the library actually carries aliases for it.
 *    Aliases reach a part row only via Save to Library, so this doubles as the
 *    "is this asset in the library" gate that would otherwise cost an
 *    `AssetVersion` fetch per asset.
 *  - The comparison itself is DIRECTIONAL (`libraryAliasAdditions`), not
 *    `!aliasesEqual`. This is the rule that actually protects local work, and
 *    the skip-gate above is not enough on its own: a part the library HAS
 *    aliases for is still one the user can add a language to, and a symmetric
 *    compare would then announce "changed in the Library" about a library that
 *    never moved. Only an entry the library has and this project lacks counts.
 *  - The repair is a MERGE, not a replace (`mergeLibraryAliases`), so even a
 *    stale report can never delete a local-only language.
 *  - The cost of the skip-gate, stated so nobody "fixes" it by widening: a part
 *    whose aliases were removed WHOLESALE in the library is not reported, since
 *    there is no library-side list left to notice. Every addition and rename
 *    still is.
 *
 * Direction is always library → project. "Update" means adopt the library's
 * names; pushing local names the other way is what Save to Library is for.
 */

import {
  aliasesEqual,
  libraryAliasAdditions,
  mergeLibraryAliases,
  type PartAlias,
} from "@models/data/aliasData";
import type { EntityData, EntitySnapshot } from "@models/data/entityData";
import type { ProjectModel } from "./ProjectModel";

/** One snapshot whose aliases differ from its library part's. */
export interface AliasSyncPart {
  readonly snapshotId: string;
  /** Snapshot display name, for the "which changes" copy. */
  readonly label: string;
  readonly localAliases: ReadonlyArray<PartAlias>;
  readonly libraryAliases: ReadonlyArray<PartAlias>;
}

/** One entity (a placed model) carrying at least one drifted part. */
export interface AliasSyncEntity {
  readonly sceneId: string;
  readonly entityId: string;
  readonly assetId: string;
  /** Root snapshot's display name — what the user calls this model. */
  readonly label: string;
  readonly parts: ReadonlyArray<AliasSyncPart>;
}

/** Everything in the project that is behind its library entry. */
export interface AliasSyncReport {
  readonly projectId: string;
  /**
   * Scenes this report covers. Needed because {@link AliasSyncReport.signature}
   * describes the LIBRARY side only, so two scenes holding placements of the
   * same drifted asset produce identical signatures — a consumer that
   * de-duplicates on the signature alone would silently drop the second
   * scene's report and leave it stale.
   */
  readonly sceneIds: ReadonlyArray<string>;
  readonly entities: ReadonlyArray<AliasSyncEntity>;
  /** Total drifted parts across every entity — drives the toast copy. */
  readonly partCount: number;
  /**
   * Stable fingerprint of the LIBRARY side of this report. The UI remembers
   * dismissals against it, so dismissing suppresses this exact library state
   * while a later library change produces a new signature and legitimately
   * asks again.
   */
  readonly signature: string;
}

/** Outcome of a library-alias pull. Failures are counted, not thrown. */
export interface AliasSyncResult {
  readonly applied: number;
  readonly failed: number;
}

export class ProjectModelAliases {
  /**
   * Compare every loaded scene's snapshots against the library part rows.
   * Pure over in-memory state — safe to call as often as the UI likes.
   *
   * @param sceneId Restrict to one scene; omit to cover every loaded scene.
   * @returns `null` when nothing has drifted.
   */
  getLibraryAliasSyncReport(
    this: ProjectModel,
    sceneId?: string,
  ): AliasSyncReport | null {
    const projectId = this.activeProject?.id;
    if (!projectId) return null;
    const state = internals(this);
    if (state.m_partAliasesById.size === 0) return null;

    const sceneIds = sceneId ? [sceneId] : [...state.m_entitiesByScene.keys()];
    const entities: AliasSyncEntity[] = [];
    let partCount = 0;
    const signatureParts: string[] = [];

    for (const scene of sceneIds) {
      for (const entity of state.m_entitiesByScene.get(scene) ?? []) {
        if (!entity.assetId) continue;

        const parts: AliasSyncPart[] = [];
        let label = "";
        for (const snapshot of flattenSnapshots(entity.snapshots)) {
          if (snapshot.isRoot && !label) label = snapshot.displayName || snapshot.name;
          const partId = snapshot.partId;
          if (!partId) continue;
          // `.get`, not `.get(...) ?? []` — a part the library has no aliases
          // for is SKIPPED, not compared against an empty list.
          const libraryAliases = state.m_partAliasesById.get(partId);
          if (!libraryAliases) continue;
          // Directional: only entries the LIBRARY has and this project lacks.
          // A local-only addition is not drift (see the header).
          if (libraryAliasAdditions(snapshot.aliases, libraryAliases).length === 0) continue;
          parts.push({
            snapshotId: snapshot.id,
            label: snapshot.displayName || snapshot.name || partId,
            localAliases: snapshot.aliases,
            libraryAliases,
          });
          signatureParts.push(`${partId}=${signatureOf(libraryAliases)}`);
        }
        if (parts.length === 0) continue;

        partCount += parts.length;
        entities.push({
          sceneId: scene,
          entityId: entity.id,
          assetId: entity.assetId,
          label: label || "Model",
          parts,
        });
      }
    }

    if (entities.length === 0) return null;
    // Sorted so an identical library state always hashes the same regardless
    // of scene / entity iteration order — otherwise a dismissal would not
    // stick across reloads.
    return {
      projectId,
      sceneIds: [...new Set(entities.map((e) => e.sceneId))],
      entities,
      partCount,
      signature: signatureParts.sort().join("|"),
    };
  }

  /**
   * Adopt the library's aliases for every part named in a report.
   *
   * Routed through `updateEntitySnapshotAsync` per snapshot rather than a bulk
   * write, so each one gets the same local-apply, persist, and broadcast the
   * inspector's own alias edit does — a peer watching this project sees the
   * pull land exactly like a manual edit.
   *
   * Re-reads the report's snapshots from the live model instead of trusting
   * the captured copy: a report can be minutes old (it sits in a toast until
   * the user acts), and a part edited in the meantime must not be reverted to
   * whatever it held when the toast appeared.
   *
   * Writes a MERGE, never the library list verbatim, so a local-only language
   * survives the pull even if the report that prompted it was stale.
   *
   * Per-snapshot failures are counted, not thrown. The toast that triggers this
   * is already dismissed by the time the promise settles, so rejecting would
   * discard the count of what DID land and leave the user told only that it
   * "failed" — with several parts silently updated.
   */
  async syncLibraryAliasesAsync(
    this: ProjectModel,
    report: AliasSyncReport,
    signal?: AbortSignal,
  ): Promise<AliasSyncResult> {
    const state = internals(this);
    let applied = 0;
    let failed = 0;

    for (const entity of report.entities) {
      for (const part of entity.parts) {
        const snapshot = this.getEntitySnapshotById(entity.sceneId, part.snapshotId);
        if (!snapshot) continue;
        // Re-resolve from the catalog rather than the captured list — the
        // library is the authority and may have moved on since the report.
        const libraryAliases = snapshot.partId
          ? state.m_partAliasesById.get(snapshot.partId)
          : undefined;
        if (!libraryAliases) continue;
        const merged = mergeLibraryAliases(snapshot.aliases, libraryAliases);
        if (aliasesEqual(snapshot.aliases, merged)) continue;
        try {
          await this.updateEntitySnapshotAsync(
            entity.sceneId,
            part.snapshotId,
            { aliases: merged },
            signal,
          );
          applied++;
        } catch (err) {
          failed++;
          console.warn(
            `[ProjectModel] alias sync failed (scene=${entity.sceneId}, snapshot=${part.snapshotId}):`,
            err,
          );
        }
      }
    }
    return { applied, failed };
  }

  /**
   * Re-run the check for a scene and emit when something has drifted. Called
   * after a scene's entities are mapped — that is the first moment both sides
   * of the comparison exist.
   */
  reportLibraryAliasDrift(this: ProjectModel, sceneId: string): void {
    const report = this.getLibraryAliasSyncReport(sceneId);
    if (report) this.onLibraryAliasesOutOfSync.emit(report);
  }
}

// ---- Helpers ---------------------------------------------------------------

/** Depth-first walk of the snapshot tree (roots carry their parts as children). */
function flattenSnapshots(
  snapshots: ReadonlyArray<EntitySnapshot>,
): ReadonlyArray<EntitySnapshot> {
  const out: EntitySnapshot[] = [];
  const walk = (list: ReadonlyArray<EntitySnapshot>): void => {
    for (const snapshot of list) {
      out.push(snapshot);
      if (snapshot.children.length > 0) walk(snapshot.children);
    }
  };
  walk(snapshots);
  return out;
}

/** Order-insensitive fingerprint, matching `aliasesEqual`'s set semantics. */
function signatureOf(aliases: ReadonlyArray<PartAlias>): string {
  return aliases
    .map((a) => `${a.language}:${a.value}`)
    .sort()
    .join(",");
}

interface AliasInternals {
  readonly m_entitiesByScene: Map<string, ReadonlyArray<EntityData>>;
  readonly m_partAliasesById: Map<string, ReadonlyArray<PartAlias>>;
}

function internals(model: ProjectModel): AliasInternals {
  return model as unknown as AliasInternals;
}
